import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { registry } from '../registry';
import { defaultConfig } from '../lib/extension';
import { ExtensionProvider, createMockSdk } from '../lib/sdk';
import type { ExtensionSdk } from '../lib/sdk';
import { Shell } from '../ui/Shell';
import { errorMessage } from '../lib/errors';

const DEFAULT_ENTRY = '{\n  "title": "Example entry",\n  "url": "https://www.vestiairecollective.com/channels/summer/"\n}';

interface HarnessSdk {
  sdk: ExtensionSdk;
  parseError: string | null;
  /** Bumped on every Apply so the extension remounts with the new SDK. */
  generation: number;
}

function buildSdk(configText: string, entryText: string, fieldValue: unknown, onFieldChange: (v: unknown) => void, generation: number): HarnessSdk {
  try {
    const config: unknown = JSON.parse(configText);
    const entry = JSON.parse(entryText) as Record<string, unknown>;
    return { sdk: createMockSdk({ config, entry, fieldValue, onFieldChange }), parseError: null, generation };
  } catch (e) {
    return { sdk: createMockSdk({ onFieldChange }), parseError: errorMessage(e), generation };
  }
}

/**
 * Local route: /#/dev/<id>. Renders the extension with a mock SDK so you can develop
 * without wiring a stack: edit the config params and the entry, watch the field value.
 */
export function DevHarnessPage() {
  const { id = '' } = useParams();
  const entry = registry[id];
  const [configText, setConfigText] = useState(() => JSON.stringify(entry ? defaultConfig(entry.meta) : {}, null, 2));
  const [entryText, setEntryText] = useState(DEFAULT_ENTRY);
  const [fieldValue, setFieldValue] = useState<unknown>('');
  const [harness, setHarness] = useState<HarnessSdk>(() => buildSdk(configText, entryText, '', setFieldValue, 0));

  if (!entry) return <p className="vc-error">Unknown extension “{id}”. <Link to="/">Index</Link></p>;
  const { Component } = entry;

  const apply = () => setHarness((h) => buildSdk(configText, entryText, fieldValue, setFieldValue, h.generation + 1));

  return (
    <div className="vc-harness">
      <div>
        <p className="vc-muted"><Link to="/">← all extensions</Link> · dev harness for <strong>{entry.meta.title}</strong> ({entry.meta.location})</p>
        <div className="vc-frame">
          <Shell>
            <ExtensionProvider key={harness.generation} sdk={harness.sdk} meta={entry.meta}>
              <Component />
            </ExtensionProvider>
          </Shell>
        </div>
      </div>
      <aside className="vc-harness__panel vc-stack">
        <label>Config params<textarea value={configText} onChange={(e) => setConfigText(e.target.value)} /></label>
        <label>Entry (sibling fields)<textarea value={entryText} onChange={(e) => setEntryText(e.target.value)} /></label>
        <button className="vc-button" onClick={apply}>Apply</button>
        {harness.parseError && <p className="vc-error">{harness.parseError}</p>}
        <div>
          <div className="vc-muted">Field value written by the extension</div>
          <pre>{JSON.stringify(fieldValue, null, 2)}</pre>
        </div>
      </aside>
    </div>
  );
}
